import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Search } from 'lucide-react';
import { vdDirectoryApi, vdDepartmentApi } from '../api';

export default function VDDirectory() {
  const [employees, setEmployees] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const { register, handleSubmit, reset, watch, setValue } = useForm();

  const load = () => Promise.all([vdDirectoryApi.list(), vdDepartmentApi.list()]).then(([e, d]) => { setEmployees(e); setDepts(d); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    reset({ fullName: '', jobTitle: '', departmentId: '', extension: '', directPhone: '', email: '', forwardingEnabled: true, active: true });
    setDialogOpen(true);
  };
  const openEdit = (emp) => { setEditing(emp); reset(emp); setDialogOpen(true); };

  const onSubmit = async (data) => {
    const dept = depts.find(d => d.id === data.departmentId);
    data.departmentName = dept?.name || '';
    if (editing) {
      await vdDirectoryApi.update(editing.id, data);
      toast.success('Employee updated');
    } else {
      await vdDirectoryApi.create(data);
      toast.success('Employee added');
    }
    setDialogOpen(false);
    load();
  };

  const onDelete = async (id) => {
    await vdDirectoryApi.remove(id);
    toast.success('Employee removed');
    load();
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || e.fullName?.toLowerCase().includes(q) || e.extension?.includes(q) || e.jobTitle?.toLowerCase().includes(q);
    const matchDept = filterDept === 'all' || e.departmentId === filterDept;
    return matchSearch && matchDept;
  });

  return (
    <div className="max-w-5xl space-y-6" data-testid="vd-directory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Employee Directory</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage employees for extension dialing and call forwarding</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-1.5" />Add Employee
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, extension..." className="pl-9 bg-card/5 border-white/10" />
        </div>
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-44 bg-card/5 border-white/10"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent className="bg-[#1a1d2e] border-white/10 text-foreground">
            <SelectItem value="all">All Departments</SelectItem>
            {depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-card/5 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card/5 border-white/10">
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Users className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">{employees.length === 0 ? 'No employees yet' : 'No matches'}</p>
            {employees.length === 0 && <Button variant="outline" size="sm" className="mt-3 border-white/10" onClick={openNew}>Add first employee</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(emp => (
            <Card key={emp.id} className="bg-card/5 border-white/10">
              <CardContent className="flex items-center justify-between py-3 px-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 text-xs font-bold shrink-0">
                    {emp.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{emp.fullName}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {emp.jobTitle || 'Employee'} &middot; Ext {emp.extension} &middot; {emp.departmentName || '—'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {emp.forwardingEnabled && <Badge variant="outline" className="border-blue-500/30 text-blue-400 text-xs">Fwd</Badge>}
                  <Badge variant="outline" className={emp.active ? 'border-emerald-500/30 text-emerald-400' : 'border-border text-muted-foreground'}>{emp.active ? 'Active' : 'Off'}</Badge>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(emp)} className="text-muted-foreground hover:text-foreground h-8 w-8"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(emp.id)} className="text-muted-foreground hover:text-red-400 h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1a1d2e] border-white/10 text-foreground sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Employee</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Full Name</Label><Input {...register('fullName', { required: true })} className="bg-card/5 border-white/10 mt-1" /></div>
              <div><Label>Job Title</Label><Input {...register('jobTitle')} className="bg-card/5 border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select value={watch('departmentId') || ''} onValueChange={v => setValue('departmentId', v)}>
                  <SelectTrigger className="bg-card/5 border-white/10 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-[#1a1d2e] border-white/10 text-foreground">
                    {depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Extension</Label><Input {...register('extension')} className="bg-card/5 border-white/10 mt-1" placeholder="101" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Direct Phone</Label><Input {...register('directPhone')} className="bg-card/5 border-white/10 mt-1" /></div>
              <div><Label>Email</Label><Input {...register('email')} className="bg-card/5 border-white/10 mt-1" /></div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Can Receive Forwarded Calls</Label>
              <Switch checked={!!watch('forwardingEnabled')} onCheckedChange={v => setValue('forwardingEnabled', v)} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={!!watch('active')} onCheckedChange={v => setValue('active', v)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-black">{editing ? 'Save' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
